export interface SettingsData {
  version: number;
  lookSensitivity: number;
  /**
   * Extra multiplier on the touch drag look rate. A thumb swipe is shorter
   * than a mouse sweep, so the pad needs its own rate on top of the shared
   * mouse sensitivity; the default is tuned to feel like a normal mobile game.
   */
  touchLookSensitivity: number;
  invertY: boolean;
  /** Flip the horizontal mouse aim, for players who prefer inverted X. */
  invertLookX: boolean;
  /**
   * Flip the lateral head-tracked offset (window and body dodge). Off by
   * default: the shipped lean direction; the toggle mirrors the whole dodge.
   */
  invertHeadTracking: boolean;
  fov: number;
  cameraMode: 'third' | 'first';
  transitionSpeed: number;
  /**
   * Step-across seams: any held movement crosses onto the neighbouring wall or
   * ceiling, head-on included, while a body coasting with no input is stopped
   * at the edge.
   */
  sidewaysTransitions: boolean;
  horizonLock: boolean;
  reducedMotion: boolean;
  headTrackingEnabled: boolean;
  headTrackingSensitivity: number;
  /** Move the body with the tracked head lean, as a bounded sway dodge. */
  headTrackingBodyShift: boolean;
  /**
   * How far a full head lean swivels the body during bullet time, metres. The
   * "Dodge swivel range" slider; the lean magnifies through this range, so a
   * bigger value sweeps the dodge further without changing how far the head
   * has to move.
   */
  dodgeSwivelRange: number;
  /** Extra multiplier on the viewer offset in third person: the arm slide. */
  thirdPersonParallax: number;
  /**
   * Hold the view on the wall the player stepped off until they settle onto
   * the new one (a head tilt, or `Q`). Off restores the automatic roll.
   */
  cameraSwivel: boolean;
  masterVolume: number;
  sfxVolume: number;
  ambienceVolume: number;
  musicVolume: number;
  muted: boolean;
  uiScale: number;
  /**
   * The on-screen touch interface: `auto` shows it on phones and tablets,
   * `on` forces it, `off` hides it (for a phone with a keyboard attached, or
   * for checking the desktop interface on the same device).
   */
  touchControls: 'auto' | 'on' | 'off';
  /**
   * Physical screen calibration for the off-axis window. Until the wizard is
   * completed (`displayCalibrated` false) the fixed balance constants are used
   * unchanged; after it, the head offset is mapped to real metres from the
   * screen size and the virtual screen sits at the player's own distance.
   */
  screenWidthCm: number;
  screenHeightCm: number;
  viewingDistanceCm: number;
  displayCalibrated: boolean;
}

const STORAGE_KEY = 'magnetic-hound.settings.v1';

export const DEFAULT_SETTINGS: SettingsData = {
  version: 1,
  lookSensitivity: 1,
  // 2.5× the touch base scale: a thumb drag is much shorter than a mouse
  // sweep, and the old fixed 1.4 left the view barely turning on a phone.
  touchLookSensitivity: 2.5,
  invertY: false,
  invertLookX: false,
  invertHeadTracking: false,
  fov: 72,
  cameraMode: 'third',
  transitionSpeed: 1,
  // On by default: stepping sideways onto a wall keeps the view direction, so
  // the world rolls about your aim axis instead of sweeping the horizon.
  sidewaysTransitions: true,
  horizonLock: false,
  reducedMotion: false,
  // On by default: the off-axis window is part of how the game is meant to be
  // seen, and the menu's camera test is there to check the setup before a run.
  // The camera itself is only asked for from a user gesture (opening that test
  // or starting a run), and every failure path turns this back off.
  headTrackingEnabled: true,
  headTrackingSensitivity: 1.5,
  headTrackingBodyShift: true,
  // The shipped bullet-time sway range; the slider spans 0.5–2 m.
  dodgeSwivelRange: 1,
  // 1 keeps the third-person window anchored like first person; raising it
  // brings back the arm slide the camera had when the window was first cut.
  thirdPersonParallax: 1,
  // On by default: a sideways step keeps the picture on the plane the player
  // stepped off, and the view swings onto the new wall only when they ask for
  // it (a head tilt, or Q). Off restores the automatic roll.
  cameraSwivel: true,
  masterVolume: 0.7,
  sfxVolume: 0.9,
  ambienceVolume: 0.35,
  musicVolume: 0.3,
  muted: false,
  uiScale: 1,
  // Detected per device: a phone gets the twin-thumb pad, a desktop keeps the
  // mouse. The setting is the escape hatch when the guess is wrong.
  touchControls: 'auto',
  // Defaults match the values the off-axis window shipped with, so an
  // uncalibrated install behaves exactly as before.
  screenWidthCm: 34,
  screenHeightCm: 19,
  viewingDistanceCm: 60,
  displayCalibrated: false,
};

/** Settings persistence with a versioned payload so old keys can never break boot. */
export class Settings {
  private data: SettingsData = { ...DEFAULT_SETTINGS };
  private stored = false;

  constructor(private readonly storage: Storage | null = safeLocalStorage()) {
    this.load();
  }

  get current(): Readonly<SettingsData> {
    return this.data;
  }

  /** True when a saved payload existed at load time. */
  get hasStoredSettings(): boolean {
    return this.stored;
  }

  get<K extends keyof SettingsData>(key: K): SettingsData[K] {
    return this.data[key];
  }

  set<K extends keyof SettingsData>(key: K, value: SettingsData[K]): void {
    if (this.data[key] === value) return;
    this.data[key] = value;
    this.save();
  }

  reset(): void {
    this.data = { ...DEFAULT_SETTINGS };
    this.save();
  }

  private load(): void {
    if (!this.storage) return;
    const raw = this.storage.getItem(STORAGE_KEY);
    if (!raw) return;
    this.stored = true;
    try {
      const parsed = JSON.parse(raw) as Partial<SettingsData>;
      this.data = migrate(parsed);
    } catch {
      this.data = { ...DEFAULT_SETTINGS };
    }
  }

  private save(): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      // Storage may be unavailable (private mode, quota). Settings simply stay in memory.
    }
  }
}

function migrate(raw: Partial<SettingsData>): SettingsData {
  const merged: SettingsData = { ...DEFAULT_SETTINGS };
  for (const key of Object.keys(DEFAULT_SETTINGS) as (keyof SettingsData)[]) {
    const value = raw[key];
    if (value !== undefined && typeof value === typeof DEFAULT_SETTINGS[key]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (merged as any)[key] = value;
    }
  }
  merged.version = DEFAULT_SETTINGS.version;
  return merged;
}

function safeLocalStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const probe = '__mh_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return null;
  }
}
